import { Capacitor } from '@capacitor/core';
import { useEffect } from 'react';

import {
  lockIonContentScroll,
  unlockIonContentScroll,
} from '@/core/lib/ion-content-scroll-lock';
import { isCapacitorNative } from '@/core/utils/capacitor.utils';

type SwipeGestureHandler = {
  canStart?: () => boolean;
  onStart?: () => void | Promise<void>;
  onEnd?: (shouldComplete: boolean) => void;
};

type PatchedSwipeHandler = SwipeGestureHandler & {
  __swipeBackScrollLockPatched?: true;
};

const PATCHED_OUTLET_KEY = '__swipeBackScrollLockOutletPatched';

function isIosNative(): boolean {
  return isCapacitorNative() && Capacitor.getPlatform() === 'ios';
}

function wrapSwipeHandler(handler: SwipeGestureHandler): PatchedSwipeHandler {
  if ((handler as PatchedSwipeHandler).__swipeBackScrollLockPatched) {
    return handler as PatchedSwipeHandler;
  }

  const wrapped: PatchedSwipeHandler = {
    canStart: () => handler.canStart?.() ?? false,
    onStart: async () => {
      lockIonContentScroll();
      try {
        await handler.onStart?.();
      } catch (error) {
        unlockIonContentScroll();
        throw error;
      }
    },
    onEnd: (shouldComplete: boolean) => {
      try {
        handler.onEnd?.(shouldComplete);
      } finally {
        unlockIonContentScroll();
      }
    },
  };
  wrapped.__swipeBackScrollLockPatched = true;
  return wrapped;
}

function patchIonRouterOutlet(outlet: HTMLIonRouterOutletElement): () => void {
  if (
    (outlet as HTMLElement & { [PATCHED_OUTLET_KEY]?: boolean })[
      PATCHED_OUTLET_KEY
    ]
  ) {
    return () => undefined;
  }
  (outlet as HTMLElement & { [PATCHED_OUTLET_KEY]?: boolean })[
    PATCHED_OUTLET_KEY
  ] = true;

  let attempts = 0;
  const tryPatchHandler = () => {
    const handler = outlet.swipeHandler as SwipeGestureHandler | undefined;
    if (!handler) {
      return false;
    }
    if ((handler as PatchedSwipeHandler).__swipeBackScrollLockPatched) {
      return true;
    }
    outlet.swipeHandler = wrapSwipeHandler(handler) as unknown as NonNullable<
      typeof outlet.swipeHandler
    >;
    return true;
  };

  if (tryPatchHandler()) {
    return () => undefined;
  }

  const intervalId = window.setInterval(() => {
    attempts += 1;
    if (tryPatchHandler() || attempts >= 50) {
      window.clearInterval(intervalId);
    }
  }, 100);

  return () => window.clearInterval(intervalId);
}

/**
 * Prevents IonContent from scrolling while the native iOS swipe-back gesture is active.
 */
export function useIosSwipeBackScrollLock(): void {
  useEffect(() => {
    if (!isIosNative()) {
      return;
    }

    const outletCleanups = new Map<HTMLIonRouterOutletElement, () => void>();

    const observeOutlets = () => {
      document.querySelectorAll('ion-router-outlet').forEach((outlet) => {
        const element = outlet as HTMLIonRouterOutletElement;
        if (outletCleanups.has(element)) {
          return;
        }
        outletCleanups.set(element, patchIonRouterOutlet(element));
      });
    };

    observeOutlets();

    const observer = new MutationObserver(observeOutlets);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      outletCleanups.forEach((cleanup) => cleanup());
      outletCleanups.clear();
      unlockIonContentScroll();
    };
  }, []);
}
